import { FormEvent, useMemo, useState } from "react";
import { ArrowRight, KeyRound } from "lucide-react";

import { apiPost } from "../lib/api";
import { Button, Card, LinkButton, StatusPill } from "./ui";

type ResetState = "idle" | "submitting" | "success" | "error";

export function ResetPasswordPage() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get("token") ?? "", []);
  const initialEmail = useMemo(() => new URLSearchParams(window.location.search).get("email") ?? "", []);
  const [state, setState] = useState<ResetState>("idle");
  const [error, setError] = useState("");
  const [requestSent, setRequestSent] = useState(false);

  async function handleRequestReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setState("submitting");
    setError("");

    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") || "");

    try {
      await apiPost<{ sent: true }>("/auth/request-password-reset", { email });
      setRequestSent(true);
      setState("success");
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "Password reset email could not be sent.");
      setState("error");
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setState("submitting");
    setError("");

    const form = new FormData(formElement);
    const password = String(form.get("password") || "");
    const confirmPassword = String(form.get("confirmPassword") || "");

    if (!token) {
      setError("This reset link is missing a token.");
      setState("error");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      setState("error");
      return;
    }

    try {
      await apiPost<{ reset: true }>("/auth/reset-password", { token, password });
      formElement.reset();
      setState("success");
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : "Password could not be reset.");
      setState("error");
    }
  }

  return (
    <main className="relative min-h-screen overflow-hidden pt-16">
      <div className="download-bg absolute inset-0 z-0 bg-bg">
        <div className="absolute inset-0 bg-gt-grid opacity-45" />
        <div className="absolute inset-0 bg-bg/34" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent to-bg" />
      </div>

      <section className="relative z-10 mx-auto grid min-h-[calc(100vh-4rem)] max-w-4xl place-items-center px-6 py-24">
        <Card interactive={false} className="w-full p-8">
          <div className="flex items-start justify-between gap-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-bg text-primary">
              <KeyRound size={26} strokeWidth={1.6} />
            </div>
            <StatusPill status={state === "success" ? "success" : state === "error" ? "warning" : "info"} label="ACCOUNT" />
          </div>

          <h1 className="mt-10 font-display text-5xl font-bold uppercase leading-none text-text">
            {token ? "Reset Password" : "Forgot Password"}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted">
            {token
              ? "Choose a new password for your iBox account, then return to the desktop app and sign in."
              : "Enter your account email and we will send a secure reset link if an iBox account exists."}
          </p>

          {!token ? (
            requestSent ? (
              <div className="mt-8">
                <p className="mb-5 text-sm font-semibold text-success">If that email has an iBox account, a reset link is on the way.</p>
                <LinkButton href="/sign-in">
                  Back to Sign In <ArrowRight size={14} />
                </LinkButton>
              </div>
            ) : (
              <form className="mt-8 space-y-5" onSubmit={handleRequestReset}>
                <label className="block space-y-2">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Email</span>
                  <input
                    required
                    type="email"
                    name="email"
                    autoComplete="email"
                    defaultValue={initialEmail}
                    className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                    placeholder="you@example.com"
                  />
                </label>

                {state === "error" ? <p className="text-sm font-semibold text-error">{error}</p> : null}

                <div className="flex flex-col gap-3 sm:flex-row">
                  <Button className="flex-1" type="submit" disabled={state === "submitting"}>
                    {state === "submitting" ? "Sending" : "Send Reset Link"}
                  </Button>
                  <LinkButton href="/sign-in" variant="ghost" className="flex-1">
                    Back to Sign In
                  </LinkButton>
                </div>
              </form>
            )
          ) : state === "success" ? (
            <div className="mt-8">
              <p className="mb-5 text-sm font-semibold text-success">Password updated. You can sign in with the new password.</p>
              <LinkButton href="/sign-in">
                Sign In <ArrowRight size={14} />
              </LinkButton>
            </div>
          ) : (
            <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
              <label className="block space-y-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">New Password</span>
                <input
                  required
                  type="password"
                  name="password"
                  minLength={10}
                  className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                  placeholder="At least 10 characters"
                />
              </label>
              <label className="block space-y-2">
                <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-text">Confirm Password</span>
                <input
                  required
                  type="password"
                  name="confirmPassword"
                  className="w-full rounded-md border border-border bg-surface-2 px-4 py-3 text-sm text-text outline-none transition-colors placeholder:text-muted focus:border-primary"
                  placeholder="Repeat the new password"
                />
              </label>

              {state === "error" ? <p className="text-sm font-semibold text-error">{error}</p> : null}

              <div className="flex flex-col gap-3 sm:flex-row">
                <Button className="flex-1" type="submit" disabled={state === "submitting"}>
                  {state === "submitting" ? "Updating" : "Update Password"}
                </Button>
                <LinkButton href="/contact" variant="ghost" className="flex-1">
                  Contact Support
                </LinkButton>
              </div>
            </form>
          )}
        </Card>
      </section>
    </main>
  );
}
